import React from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Avatar,
  LinearProgress,
} from "@mui/material";
import {
  Assignment as WorkoutIcon,
  CheckCircle as CompleteIcon,
  Schedule as ScheduleIcon,
  Timer as TimerIcon,
} from "@mui/icons-material";
import moment from "moment";
import {
  StyledTrainingCard,
  TrainingHeader,
  TrainingContent,
  ActionButton,
} from "./StyledComponents";
import NewColors from "../../../../../../themes/NewColors";

const TodaysTrainingCard = ({
  todaysWorkout,
  workoutStatus,
  activeExercise,
  timeElapsed,
  exercisesFromReport,
  completedSets,
  totalSets,
  reportData,
  activeSet,
  activeSetIndex,
  currentWorkout,
}) => {
  // Get protocol information from currentWorkout
  const protocolName = reportData?.protocol?.name || "";
  const splitName = reportData?.split?.name || "";

  // Format workout set values to handle undefined values
  const formatReps = (reps) => {
    if (reps === undefined || reps === null) return "Reps: --";
    // Handle arrays - take the first element if it's an array
    if (Array.isArray(reps)) {
      const firstRep = reps[0];
      if (firstRep === undefined || firstRep === null) return "Reps: --";
      const repsNumber = typeof firstRep === 'string' ? parseInt(firstRep, 10) : Number(firstRep);
      if (isNaN(repsNumber) || repsNumber <= 0) return "Reps: --";
      return `Reps: ${repsNumber}`;
    }
    // Handle objects - extract the reps value if it's an object
    if (typeof reps === 'object' && reps !== null) {
      const repsValue = reps.reps || reps.target || reps.value;
      if (repsValue === undefined || repsValue === null) return "Reps: --";
      const repsNumber = typeof repsValue === 'string' ? parseInt(repsValue, 10) : Number(repsValue);
      if (isNaN(repsNumber) || repsNumber <= 0) return "Reps: --";
      return `Reps: ${repsNumber}`;
    }
    // Ensure reps is a number and convert to string to avoid concatenation issues
    const repsNumber = typeof reps === 'string' ? parseInt(reps, 10) : Number(reps);
    if (isNaN(repsNumber) || repsNumber <= 0) return "Reps: --";
    return `Reps: ${repsNumber}`;
  };

  const formatWeight = (weight) => {
    if (weight === undefined || weight === null || weight <= 0) return null;
    return `${weight}kg`;
  };

  const formatTempo = (tempo) => {
    if (tempo === undefined || tempo === null) return "Tempo: --";
    return `Tempo: ${tempo}`;
  };

  const formatRest = (rest) => {
    if (rest === undefined || rest === null) return "Rest: --";
    return `Rest: ${rest}`;
  };

  // Check if we have valid weight to display
  const showWeight = !!(activeSet?.weight && activeSet.weight > 0);

  return (
    <StyledTrainingCard>
      <TrainingHeader>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <WorkoutIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Today&apos;s Training
          </Typography>
        </Stack>
        {workoutStatus === "complete" ? (
          <Chip
            label="Completed"
            color="success"
            size="small"
            icon={<CompleteIcon />}
          />
        ) : activeExercise ? (
          <Chip
            label="In Progress"
            color="warning"
            size="small"
            sx={{ animation: "pulse 2s infinite" }}
          />
        ) : todaysWorkout ? (
          <Chip label="Scheduled" color="info" size="small" />
        ) : null}
      </TrainingHeader>
      <TrainingContent>
        {todaysWorkout ? (
          <Stack spacing={3}>
            {/* Protocol & Split Information */}
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "primary.50" }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                {splitName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {protocolName}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                {reportData?.split?.description}
              </Typography>
            </Box>

            {/* Overall Workout Progress */}
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Overall Progress
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {completedSets}/{totalSets} sets
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={totalSets > 0 ? (completedSets / totalSets) * 100 : 0}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  backgroundColor: NewColors.sectionBackground,
                  "& .MuiLinearProgress-bar": {
                    background:
                      workoutStatus === "complete"
                        ? `linear-gradient(45deg, ${NewColors.status.success} 30%, ${NewColors.status.success} 90%)`
                        : `linear-gradient(45deg, ${NewColors.charts.blue} 30%, ${NewColors.active.secondary} 90%)`,
                  },
                }}
              />
            </Box>

            {/* Time Elapsed */}
            {timeElapsed !== null && workoutStatus !== "complete" && (
              <Box sx={{ p: 1.5, bgcolor: "info.50", borderRadius: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TimerIcon fontSize="small" color="info" />
                  <Typography variant="body2" fontWeight="bold">
                    Active for {timeElapsed} minutes
                  </Typography>
                </Stack>
              </Box>
            )}

            {/* Active Exercise or Completion Status */}
            {workoutStatus === "complete" ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "success.50",
                  textAlign: "center",
                }}
              >
                <CompleteIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" fontWeight="bold" color="success.dark">
                  Workout Completed! 🎉
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed:{" "}
                  {currentWorkout.completedOn
                    ? moment(currentWorkout.completedOn).format(
                        "MMM D, YYYY [at] h:mm A",
                      )
                    : "Just now"}
                </Typography>
              </Box>
            ) : activeExercise ? (
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: "warning.50" }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="warning.dark"
                  sx={{ mb: 1 }}
                >
                  Currently performing:
                </Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                  {activeExercise.exerciseName || "Exercise"}
                </Typography>

                {activeSet && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ mb: 1 }}
                    >
                      Set {activeSetIndex || 1} of {activeExercise.sets?.length || 0}
                      :
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Chip
                        label={formatReps(activeSet?.reps)}
                        variant="outlined"
                        color="primary"
                      />
                      {showWeight && (
                        <Chip
                          label={formatWeight(activeSet.weight)}
                          variant="outlined"
                          color="secondary"
                        />
                      )}
                      <Chip
                        label={formatTempo(activeSet.tempo)}
                        variant="outlined"
                        color="info"
                      />
                      <Chip
                        label={formatRest(activeSet.rest)}
                        variant="outlined"
                        color="success"
                      />
                    </Stack>
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "info.50",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  color="info.dark"
                  sx={{ mb: 1 }}
                >
                  Workout Scheduled
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {exercisesFromReport?.length ?? 0} exercises planned
                </Typography>
              </Box>
            )}

            {/* Exercise Summary */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                Today&apos;s Exercises:
              </Typography>
              <Stack spacing={1}>
                {exercisesFromReport.slice(0, 3).map((exercise, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    alignItems="center"
                    spacing={2}
                  >
                    <Chip
                      icon={
                        exercise.completed ? <CompleteIcon /> : <ScheduleIcon />
                      }
                      label={exercise.exerciseName || exercise.superSetName}
                      color={exercise.completed ? "success" : "default"}
                      variant="outlined"
                      size="small"
                    />
                    {exercise.clientAdded === true && (
                      <Chip label="Added" color="warning" size="small" variant="outlined" />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {exercise.sets?.length} sets
                    </Typography>
                  </Stack>
                ))}
                {exercisesFromReport.length > 3 && (
                  <Typography variant="caption" color="text.secondary">
                    +{exercisesFromReport.length - 3} more exercises
                  </Typography>
                )}
              </Stack>
            </Box>

            {/* Workout Management */}
            <Stack direction="row" spacing={1}>
              <ActionButton
                variant="outlined"
                size="small"
                disabled
                startIcon={<ScheduleIcon />}
                sx={{ flex: 1 }}
              >
                Client&apos;s Session
              </ActionButton>
            </Stack>
          </Stack>
        ) : (
          <Box sx={{ textAlign: "center", p: 2 }}>
            <Avatar
              sx={{
                width: 60,
                height: 60,
                bgcolor: "grey.100",
                mx: "auto",
                mb: 2,
              }}
            >
              <ScheduleIcon sx={{ fontSize: 32, color: "grey.400" }} />
            </Avatar>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No Workout Scheduled
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Client has no exercises planned for today
            </Typography>
          </Box>
        )}
      </TrainingContent>
    </StyledTrainingCard>
  );
};

export default TodaysTrainingCard;
